import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { playlistsApi } from '../api/playlists';
import { toast } from 'sonner';
import offlineDB from '../utils/offlineDB';
import logger from '@/utils/logger';

// Aviso de modo offline una sola vez por episodio (se rearma al volver a cargar online)
let offlineToastShown = false;
const notifyOffline = (msg) => {
  if (offlineToastShown) return;
  offlineToastShown = true;
  toast.info(msg, { duration: 4000 });
};

// Query keys
export const playlistKeys = {
  all: ['playlists'],
  detail: (id) => ['playlists', id],
  calendar: ['playlists', 'calendar'],
};

// Get all playlists - with offline fallback
export const usePlaylists = (enabled = true) => {
  return useQuery({
    queryKey: playlistKeys.all,
    queryFn: async () => {
      const isOnline = navigator.onLine;
      // Try online first
      if (isOnline) {
        try {
          const data = await playlistsApi.getAll();
          offlineToastShown = false;
          // Cache the data for offline use
          if (data && data.length > 0) {
            offlineDB.savePlaylists(data).catch(err =>
              console.warn('[usePlaylists] Failed to cache playlists:', err)
            );
          }
          return data;
        } catch (error) {
          console.warn('[usePlaylists] API failed, intentando cache offline:', error.message);
          // Iter91.11: si la API falla y NO hay cache disponible, NO devolvemos []
          // silenciosamente — eso provocaba el bug "eventos a 0" tras cold start
          // del backend o cuando iOS suspende la fetch al reactivar PWA.
          // En su lugar, throw para que React Query reintente y la UI muestre error.
          const cachedPlaylists = await offlineDB.getAllPlaylists().catch(() => []);
          if (cachedPlaylists && cachedPlaylists.length > 0) {
            logger.log(`[usePlaylists] Usando ${cachedPlaylists.length} playlists del cache (API offline temporalmente)`);
            notifyOffline(`📴 Modo offline: ${cachedPlaylists.length} eventos desde caché`);
            return cachedPlaylists;
          }
          // Sin cache y online → re-throw para que React Query retry
          throw error;
        }
      }

      // Realmente offline (sin red): solo cache
      logger.log('[usePlaylists] Sin red — cargando desde cache offline...');
      const cachedPlaylists = await offlineDB.getAllPlaylists();
      if (cachedPlaylists && cachedPlaylists.length > 0) {
        logger.log(`[usePlaylists] Found ${cachedPlaylists.length} playlists in cache`);
        notifyOffline(`📴 Modo offline: ${cachedPlaylists.length} eventos desde caché`);
        return cachedPlaylists;
      }
      logger.log('[usePlaylists] No cached playlists available');
      return [];
    },
    staleTime: 1000 * 30, // 30 seconds - faster updates for team members
    refetchOnMount: true,
    refetchOnWindowFocus: true, // Refetch when tab gets focus
    refetchInterval: 1000 * 60, // Auto-refetch every 60 seconds
    enabled: enabled,
    // Iter91.11: retry agresivo para cold start del backend (5-15s típico)
    retry: (failureCount, error) => {
      if (!navigator.onLine) return false;
      return failureCount < 4;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 8000), // 1s, 2s, 4s, 8s
  });
};

// Get playlist by ID - with offline fallback
export const usePlaylist = (playlistId) => {
  return useQuery({
    queryKey: playlistKeys.detail(playlistId),
    queryFn: async () => {
      // Try online first
      if (navigator.onLine) {
        try {
          return await playlistsApi.getById(playlistId);
        } catch (error) {
          console.warn('[usePlaylist] API failed, falling back to offline:', error.message);
        }
      }
      
      // Offline fallback
      logger.log('[usePlaylist] Loading from offline cache...');
      const cachedPlaylist = await offlineDB.getPlaylistById(playlistId);
      
      if (cachedPlaylist) {
        logger.log('[usePlaylist] Found playlist in cache');
        notifyOffline('📴 Evento cargado desde caché offline');
        return cachedPlaylist;
      }
      
      throw new Error('Playlist not found in offline cache');
    },
    enabled: !!playlistId,
    retry: (failureCount, error) => {
      if (!navigator.onLine) return false;
      return failureCount < 1;
    },
  });
};

// Get calendar events - with offline fallback
export const useCalendarEvents = () => {
  return useQuery({
    queryKey: playlistKeys.calendar,
    queryFn: async () => {
      // Try online first
      if (navigator.onLine) {
        try {
          return await playlistsApi.getCalendarEvents();
        } catch (error) {
          console.warn('[useCalendarEvents] API failed, falling back to offline:', error.message);
        }
      }
      
      // Offline fallback - transform cached playlists to calendar format
      logger.log('[useCalendarEvents] Loading from offline cache...');
      const cachedPlaylists = await offlineDB.getAllPlaylists();
      
      if (cachedPlaylists && cachedPlaylists.length > 0) {
        // Transform to calendar event format
        return cachedPlaylists.map(p => ({
          id: p.id,
          title: p.nombre_novios || 'Evento',
          date: p.fecha_evento,
          start: p.hora_inicio,
          end: p.hora_fin,
          type: 'event',
          lugar: p.lugar_evento,
          dj: p.nombre_dj
        }));
      }
      
      return [];
    },
    retry: (failureCount, error) => {
      if (!navigator.onLine) return false;
      return failureCount < 1;
    },
  });
};

// Create playlist mutation
export const useCreatePlaylist = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: playlistsApi.create,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: playlistKeys.all });
      queryClient.invalidateQueries({ queryKey: playlistKeys.calendar });
      toast.success('Evento creado correctamente');
      return data;
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al crear evento');
    },
  });
};

// Update playlist mutation
export const useUpdatePlaylist = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: playlistsApi.update,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: playlistKeys.all });
      queryClient.invalidateQueries({ queryKey: playlistKeys.detail(variables.playlistId) });
      queryClient.invalidateQueries({ queryKey: playlistKeys.calendar });
      toast.success('Evento actualizado');
      return data;
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al actualizar evento');
    },
  });
};

// Delete playlist mutation
export const useDeletePlaylist = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: playlistsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: playlistKeys.all });
      queryClient.invalidateQueries({ queryKey: playlistKeys.calendar });
      toast.success('Evento eliminado');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al eliminar evento');
    },
  });
};

// Helper function to generate custom list offline
const generateCustomListOffline = async (playlistId) => {
  logger.log('[Offline] Generating custom list locally...');
  
  // Get playlist data
  const playlist = await offlineDB.getPlaylistById(playlistId);
  if (!playlist) {
    throw new Error('Evento no encontrado en cache');
  }
  
  // Get all cached songs
  const allSongs = await offlineDB.getAllSongs();
  if (!allSongs || allSongs.length === 0) {
    throw new Error('No hay canciones en cache. Sincroniza primero.');
  }
  
  const duracionBaile = parseFloat(playlist.duracion_baile) || 3;
  const gustosMusicales = (playlist.gustos_musicales || '').toLowerCase();
  const cancionesProhibidas = (playlist.canciones_prohibidas || '').toLowerCase();
  const porcentajesEstilos = playlist.porcentajes_estilos || '';
  
  // Target: 20 songs per hour
  const targetSongs = Math.ceil(duracionBaile * 20);
  
  // Normalize text for comparison
  const normalize = (text) => {
    if (!text) return '';
    return text.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  };
  
  // Check if song is prohibited
  const isProhibited = (song) => {
    if (!cancionesProhibidas) return false;
    const titleNorm = normalize(song.title);
    const artistNorm = normalize(song.artist);
    return cancionesProhibidas.split(',').some(term => {
      const termNorm = normalize(term);
      return titleNorm.includes(termNorm) || artistNorm.includes(termNorm);
    });
  };
  
  // Filter out prohibited songs
  const availableSongs = allSongs.filter(song => !isProhibited(song));
  
  // Sort by priority: favorito > 5★ > 4★ > 3★ > 2★ > 1★
  const sortByPriority = (songs) => {
    return [...songs].sort((a, b) => {
      const aScore = (a.favorito ? 100 : 0) + ((a.rating || 0) * 10);
      const bScore = (b.favorito ? 100 : 0) + ((b.rating || 0) * 10);
      return bScore - aScore;
    });
  };
  
  let selectedSongs = [];
  
  // Parse porcentajes if specified
  const porcentajesObj = {};
  if (porcentajesEstilos) {
    porcentajesEstilos.split(',').forEach(item => {
      const match = item.trim().match(/(\d+)%?\s*(.+)/);
      if (match) {
        porcentajesObj[normalize(match[2])] = parseInt(match[1]);
      }
    });
  }
  
  const hasPortentajes = Object.keys(porcentajesObj).length > 0;
  
  if (hasPortentajes) {
    logger.log('[Offline] Using porcentajes:', porcentajesObj);
    
    const specifiedGenres = Object.keys(porcentajesObj).filter(g => g !== 'otros');
    
    // First pass: specified genres
    for (const [estilo, porcentaje] of Object.entries(porcentajesObj)) {
      if (estilo === 'otros') continue;
      
      const numNeeded = Math.ceil((targetSongs * porcentaje) / 100);
      const estiloSongs = availableSongs.filter(s => {
        const genre = normalize(s.genero || '');
        return genre.includes(estilo) && !selectedSongs.find(sel => sel.id === s.id);
      });
      
      const sorted = sortByPriority(estiloSongs);
      selectedSongs.push(...sorted.slice(0, numNeeded));
    }
    
    // Second pass: "otros" - songs from genres NOT in specified list
    const otrosPorcentaje = porcentajesObj['otros'] || 0;
    if (otrosPorcentaje > 0) {
      const numOtrosNeeded = Math.ceil((targetSongs * otrosPorcentaje) / 100);
      const otrosSongs = availableSongs.filter(s => {
        const genre = normalize(s.genero || '');
        const isSpecified = specifiedGenres.some(spec => genre.includes(spec));
        return !isSpecified && !selectedSongs.find(sel => sel.id === s.id);
      });
      
      const sorted = sortByPriority(otrosSongs);
      selectedSongs.push(...sorted.slice(0, numOtrosNeeded));
    }
  } else {
    // No porcentajes - auto-distribute by genre quality
    logger.log('[Offline] Auto-distributing by genre quality');
    
    // Analyze genres
    const genreStats = {};
    availableSongs.forEach(song => {
      const genre = song.genero || 'Sin género';
      if (!genreStats[genre]) {
        genreStats[genre] = { songs: [], qualityScore: 0 };
      }
      genreStats[genre].songs.push(song);
      genreStats[genre].qualityScore += (song.favorito ? 10 : 0) + ((song.rating || 0) >= 5 ? 5 : (song.rating || 0) >= 4 ? 3 : 1);
    });
    
    // Sort genres by quality score
    const sortedGenres = Object.entries(genreStats)
      .sort((a, b) => b[1].qualityScore - a[1].qualityScore);
    
    const totalQualityScore = sortedGenres.reduce((sum, [, stats]) => sum + stats.qualityScore, 0);
    
    // Distribute proportionally
    for (const [genre, stats] of sortedGenres) {
      if (selectedSongs.length >= targetSongs) break;
      
      const proportion = stats.qualityScore / totalQualityScore;
      const numFromGenre = Math.max(1, Math.ceil(targetSongs * proportion));
      
      const genreSongs = stats.songs.filter(s => !selectedSongs.find(sel => sel.id === s.id));
      const sorted = sortByPriority(genreSongs);
      selectedSongs.push(...sorted.slice(0, numFromGenre));
    }
  }
  
  // Fill remaining with best available
  if (selectedSongs.length < targetSongs) {
    const remaining = sortByPriority(
      availableSongs.filter(s => !selectedSongs.find(sel => sel.id === s.id))
    );
    selectedSongs.push(...remaining.slice(0, targetSongs - selectedSongs.length));
  }
  
  // Trim to target
  selectedSongs = selectedSongs.slice(0, targetSongs);
  
  logger.log(`[Offline] Generated list with ${selectedSongs.length} songs`);
  
  return {
    songs: selectedSongs,
    total: selectedSongs.length,
    duration_minutes: Math.round(selectedSongs.length * 3),
    generated_offline: true
  };
};

// Generate custom list mutation - with offline support
export const useGenerateCustomList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (playlistId) => {
      // Try online first
      if (navigator.onLine) {
        try {
          return await playlistsApi.generateCustomList(playlistId);
        } catch (error) {
          console.warn('[useGenerateCustomList] API failed, trying offline:', error.message);
        }
      }
      
      // Offline fallback
      return await generateCustomListOffline(playlistId);
    },
    onSuccess: (data, playlistId) => {
      queryClient.invalidateQueries({ queryKey: playlistKeys.detail(playlistId) });
      if (data.generated_offline) {
        toast.warning('🔴 Lista generada offline - Se sincronizará cuando vuelva la conexión');
      }
      return data;
    },
    onError: (error) => {
      toast.error(error.message || 'Error al generar lista personalizada');
    },
  });
};

// Generate AI recommendations mutation
export const useGenerateAIRecommendations = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: playlistsApi.generateAIRecommendations,
    onSuccess: (data, playlistId) => {
      queryClient.invalidateQueries({ queryKey: playlistKeys.detail(playlistId) });
      return data;
    },
  });
};

// Add song to custom list mutation
export const useAddSongToCustomList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: playlistsApi.addSongToCustomList,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: playlistKeys.detail(variables.playlistId) });
      toast.success('Canción añadida a la lista');
      return data;
    },
    onError: (error) => {
      const message = error.response?.data?.detail || 'Error al añadir canción';
      if (!message.includes('ya está')) {
        toast.error(message);
      }
    },
  });
};

// Remove song from custom list mutation
export const useRemoveSongFromCustomList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: playlistsApi.removeSongFromCustomList,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: playlistKeys.detail(variables.playlistId) });
      return data;
    },
  });
};

// Generate access code mutation
export const useGenerateAccessCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: playlistsApi.generateAccessCode,
    onSuccess: (data, playlistId) => {
      queryClient.invalidateQueries({ queryKey: playlistKeys.detail(playlistId) });
      return data;
    },
  });
};
